/**
 * Identity Dashboard page
 * Overview of subjects, IdP connections, and recent sync activity
 */

import { useNavigate } from 'react-router-dom'
import {
  RefreshCw,
  AlertCircle,
  Users,
  Link2,
  Activity,
  UserPlus,
  Plus,
} from 'lucide-react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { MetricCard } from '@/components/dashboard'
import { useSubjects } from '@/hooks/use-subjects'
import { useIdPConnections } from '@/hooks/use-idp-connections'
import { useSyncRuns } from '@/hooks/use-sync-runs'
import { SyncStatusBadge } from '@/components/identity/sync-status-badge'
import { cn } from '@/lib/utils'

function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

export function IdentityDashboardPage() {
  const navigate = useNavigate()

  const {
    data: subjectsData,
    isLoading: subjectsLoading,
    isError: subjectsError,
    error: subjectsErr,
    refetch: refetchSubjects,
  } = useSubjects({ page: 1, pageSize: 1 })

  const {
    data: connections,
    isLoading: connectionsLoading,
    refetch: refetchConnections,
  } = useIdPConnections()

  const {
    data: syncRunsData,
    isLoading: syncRunsLoading,
    refetch: refetchSyncRuns,
  } = useSyncRuns({ page: 1, pageSize: 5 })

  const isLoading = subjectsLoading || connectionsLoading || syncRunsLoading

  const handleRefresh = () => {
    refetchSubjects()
    refetchConnections()
    refetchSyncRuns()
  }

  const totalSubjects = subjectsData?.pagination?.totalItems ?? 0
  const connectionList = connections ?? []
  const connectedCount = connectionList.filter((c) => c.status === 'CONNECTED').length
  const failedCount = connectionList.filter((c) => c.status === 'FAILED').length
  const recentSyncRuns = syncRunsData?.data ?? []

  return (
    <>
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-base font-medium">Identity Store</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
            className="h-8"
          >
            <RefreshCw
              className={cn('mr-2 h-3.5 w-3.5', isLoading && 'animate-spin')}
            />
            Refresh
          </Button>
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        <div>
          <p className="text-sm text-muted-foreground">
            Manage user identities, attributes, and IdP synchronization
          </p>
        </div>

        {subjectsError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                Failed to load metrics:{' '}
                {subjectsErr instanceof Error ? subjectsErr.message : 'Unknown error'}
              </span>
              <Button variant="outline" size="sm" onClick={handleRefresh} className="ml-4">
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Metric Cards */}
        <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Total Subjects"
            value={totalSubjects}
            description="Registered user identities"
            icon={Users}
            isLoading={subjectsLoading}
          />
          <MetricCard
            title="IdP Connections"
            value={connectionList.length}
            description={`${connectedCount} connected`}
            icon={Link2}
            isLoading={connectionsLoading}
          />
          <MetricCard
            title="Connected"
            value={connectedCount}
            description="Active IdP connections"
            icon={Activity}
            isLoading={connectionsLoading}
          />
          <MetricCard
            title="Failed"
            value={failedCount}
            description="Connections needing attention"
            icon={AlertCircle}
            isLoading={connectionsLoading}
          />
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/identity/subjects')}
          >
            <UserPlus className="mr-2 h-3.5 w-3.5" />
            Manage Subjects
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/identity/connections')}
          >
            <Plus className="mr-2 h-3.5 w-3.5" />
            Configure IdP
          </Button>
        </div>

        {/* Recent Sync Runs + IdP Connections */}
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Recent Sync Runs */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Recent Sync Runs</CardTitle>
                  <CardDescription>Latest IdP synchronization activity</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {syncRunsLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : recentSyncRuns.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-muted-foreground">
                    No sync runs yet. Configure an IdP connection to get started.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentSyncRuns.map((run) => (
                    <div
                      key={run.id}
                      className="flex items-center justify-between gap-3 rounded-md border p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">
                          {run.idpConnection?.name ?? 'Unknown'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {run.trigger} &middot; {run.totalSubjects} subjects
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <SyncStatusBadge status={run.status} />
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(run.createdAt)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* IdP Connections */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>IdP Connections</CardTitle>
                  <CardDescription>Identity provider status</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/identity/connections')}
                >
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {connectionsLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : connectionList.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-muted-foreground">
                    No IdP connections configured.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => navigate('/identity/connections')}
                  >
                    Add Connection
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {connectionList.slice(0, 5).map((conn) => (
                    <div
                      key={conn.id}
                      className="flex cursor-pointer items-center justify-between gap-3 rounded-md border p-3 hover:bg-muted/50"
                      onClick={() => navigate(`/identity/connections/${conn.id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          navigate(`/identity/connections/${conn.id}`)
                        }
                      }}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{conn.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {conn.providerType}
                          {conn.syncEnabled && ' \u00b7 Sync enabled'}
                        </p>
                      </div>
                      <Badge
                        variant={
                          conn.status === 'CONNECTED'
                            ? 'default'
                            : conn.status === 'FAILED'
                              ? 'destructive'
                              : 'outline'
                        }
                      >
                        {conn.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}
